/**
 * Centralized Menu Configuration System
 * 
 * This file contains all menu configuration for the application's settings menus.
 * It provides a unified system for determining which menu items should be visible
 * based on user roles, permissions, and company settings.
 * 
 * Usage:
 * - Import `getVisibleMenuItems` to get all menu items a user can see
 * - Import `getVisibleMenuItemsWithSubMenus` to get menu items with filtered submenus
 * - Import `MENU_ITEMS` to access the raw menu configuration
 * 
 * Adding new menu items:
 * 1. Add the route constant to constants/routes.ts
 * 2. Add the menu item to the MENU_ITEMS array below
 * 3. Set appropriate visibility (roles) and conditions
 * 4. Add icon mapping in ProfileMenuPopup if needed
 * 
 * The system automatically handles:
 * - Role-based visibility (CLIENT_ROLE, RECRUITER_ROLE)
 * - Custom conditions (e.g., !user?.is_sourcer)
 * - Permission-based submenu filtering
 * - Company feature flags
 */

import { CLIENT_ROLE, RECRUITER_ROLE } from '../constants/roles';
import {
    PROFILE_SETTINGS,
    CANDIDATES_SPECIFICATION_SETTINGS,
    PAYMENT_INFO_SETTINGS,
    NOTIFICATIONS_SETTINGS,
    ATS_SETTINGS,
    ATS_DEFAULT_MAPPINGS,
    RECRUITER_COMPANY_SETTINGS,
    RECRUITER_COMPANY_TYPE_SETTINGS,
    RECRUITER_COMPANY_USERS_SETTINGS,
    PERMISSIONS_SETTINGS,
    PAYMENTS,
} from '../constants/routes';

// Type definitions for menu items and submenus
export interface SubMenuItem {
    title: string;
    value: string;
    route: string;
    requiresPermission?: string;
    condition?: (user: any) => boolean;
}

export interface MenuItem {
    title: string;
    value: string;
    visibility: string[];
    route: string;
    condition?: (user: any) => boolean;
    subMenus?: SubMenuItem[];
}

// Centralized menu configuration
export const MENU_ITEMS: MenuItem[] = [
    {
        title: 'PROFILE_SETTINGS',
        value: 'profile',
        visibility: [CLIENT_ROLE, RECRUITER_ROLE],
        route: PROFILE_SETTINGS,
    },
    {
        title: 'CANDIDATE_TITLE',
        value: 'candidate',
        visibility: [RECRUITER_ROLE],
        route: CANDIDATES_SPECIFICATION_SETTINGS,
    },
    {
        title: 'RECRUITER_COMPANY_SETTINGS',
        value: 'recruiter-company',
        visibility: [RECRUITER_ROLE],
        route: RECRUITER_COMPANY_SETTINGS,
        condition: (user: any) => !user?.is_sourcer,
        subMenus: [
            {
                title: 'COMPANY_TYPE',
                value: 'recruiter-company-type',
                route: RECRUITER_COMPANY_TYPE_SETTINGS,
                condition: (user: any) => !user?.is_sourcer,
            },
            {
                title: 'COMPANY_USERS',
                value: 'recruiter-company-users',
                route: RECRUITER_COMPANY_USERS_SETTINGS,
                condition: (user: any) => !user?.is_sourcer && user?.recruiter_type && typeof user.recruiter_type === 'string' && user.recruiter_type !== 'freelance' && user?.company,
            },
        ],
    },
    {
        title: 'PAYMENT_INFO',
        value: 'payment',
        visibility: [CLIENT_ROLE, RECRUITER_ROLE],
        route: PAYMENT_INFO_SETTINGS,
    },
    {
        title: 'PORTAL_SETTINGS',
        value: 'permissions',
        visibility: [CLIENT_ROLE],
        route: PERMISSIONS_SETTINGS,
        condition: (user: any) => {
            return user?.permissions?.view_settings === true;
        },
    },
    {
        title: 'ats.ATS_SETTINGS_TITLE',
        value: 'ats',
        visibility: [CLIENT_ROLE],
        route: ATS_SETTINGS,
        subMenus: [
            {
                title: 'ats.DEFAULT_MAPPINGS_TITLE',
                value: 'default-mappings',
                route: ATS_DEFAULT_MAPPINGS,
                requiresPermission: 'can_set_default_stage_mappings',
            },
        ],
    },
    {
        title: 'NOTIFICATION_SETTINGS',
        value: 'notification',
        visibility: [CLIENT_ROLE, RECRUITER_ROLE],
        route: NOTIFICATIONS_SETTINGS,
    },
    {
        title: 'PAYMENTS',
        value: 'payments',
        visibility: [CLIENT_ROLE, RECRUITER_ROLE],
        route: PAYMENTS,
    },
];

/**
 * Determines if a menu item should be visible based on user role and conditions
 */
export const isMenuItemVisible = (menuItem: MenuItem, user: any, company: any): boolean => {
    // Check role visibility
    if (!user?.role || menuItem.visibility.indexOf(user.role) === -1) {
        return false;
    }

    // Check custom condition
    if (typeof menuItem.condition === 'function' && !menuItem.condition(user)) {
        return false;
    }

    return true;
};

/**
 * Filters submenu items based on permissions and conditions
 */
export const getFilteredSubMenus = (subMenus: SubMenuItem[] | undefined, user: any, company: any): SubMenuItem[] => {
    if (!subMenus) return [];

    return subMenus.filter(subMenu => {
        // Check if permission is required and if the company has it
        const permissionCheck = !('requiresPermission' in subMenu) ||
            (subMenu.requiresPermission && company?.[subMenu.requiresPermission as keyof typeof company]);
        // Check if a condition exists and if it's met by the user
        const conditionCheck = !('condition' in subMenu) ||
            (subMenu.condition && subMenu.condition(user));
        // Include the submenu if both checks pass
        return permissionCheck && conditionCheck;
    });
};

/**
 * Gets all visible menu items for a user based on their role, permissions, and company settings
 */
export const getVisibleMenuItems = (user: any, company: any): MenuItem[] => {
    return MENU_ITEMS.filter(menuItem => isMenuItemVisible(menuItem, user, company));
};

/**
 * Gets all visible menu items with their filtered submenus
 */
export const getVisibleMenuItemsWithSubMenus = (user: any, company: any): MenuItem[] => {
    return getVisibleMenuItems(user, company).map(menuItem => ({
        ...menuItem,
        subMenus: getFilteredSubMenus(menuItem.subMenus, user, company),
    }));
}; 